import { Button } from '@iq/ui';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useStickyAttachmentsStore } from '../../lib/sticky-attachments-store';

interface Props {
  /** Active thread id; null when the user is on /chat with no thread bound. */
  threadId: string | null;
}

/**
 * Persistent strip above the composer that lists the uploads bound to the
 * active thread. Server-side every upload bound to the thread is
 * automatically re-injected into each chat turn until the user removes
 * it — this strip surfaces that state and exposes the delete affordance.
 *
 * The query owns the source of truth for "files bound to this thread";
 * after every successful fetch we push the totals (combined bytes +
 * image count) into `useStickyAttachmentsStore` so the upload adapter
 * (which is constructed in the runtime hook and can't read React state
 * directly) can validate the per-request caps without an extra round
 * trip to the server.
 */
export const StickyThreadAttachments: FC<Props> = ({ threadId }) => {
  const { t } = useTranslation();
  const setTotals = useStickyAttachmentsStore((s) => s.setTotals);

  const query = useQuery({
    queryKey: ['uploads', threadId],
    queryFn: async () => {
      if (!threadId) return { items: [], total: 0 };
      const { data, error } = await api.GET('/uploads', {
        params: { query: { threadId } },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!threadId,
    // The strip is the primary surface for thread-bound uploads; we want a
    // fresh count after every focus return (user came back from another tab
    // after uploading via DevTools or another browser).
    refetchOnMount: 'always',
  });

  // Push totals up to the adapter store on every data change. Wrapped in
  // `useEffect` so the write is committed AFTER render — Zustand store
  // updates during render would log "Cannot update a component while
  // rendering a different component".
  useEffect(() => {
    if (!query.data) {
      setTotals({ bytes: 0, imageCount: 0 });
      return;
    }
    const bytes = query.data.items.reduce((sum, u) => sum + u.sizeBytes, 0);
    const imageCount = query.data.items.filter((u) => u.kind === 'image').length;
    setTotals({ bytes, imageCount });
  }, [query.data, setTotals]);

  if (!threadId || !query.data || query.data.items.length === 0) return null;

  return (
    <div
      data-testid="sticky-thread-attachments"
      className="flex flex-wrap items-center gap-2 border-border border-b bg-muted/30 px-4 py-2"
    >
      <span className="text-muted-foreground text-xs" data-testid="sticky-label">
        {t('chat.attachment.stickyLabel')}
      </span>
      {query.data.items.map((u) => (
        <div
          key={u.id}
          className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs"
        >
          <span className="max-w-[160px] truncate" title={u.filename}>
            {u.filename}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            aria-label={t('chat.attachment.removeLabel', { name: u.filename })}
            onClick={async () => {
              await api.DELETE('/uploads/{uploadId}', {
                params: { path: { uploadId: u.id } },
              });
              await query.refetch();
            }}
          >
            <XIcon className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );
};
